/* eslint-disable camelcase */
import {IItemContent, IWishlistItem} from "../typescript-library";

declare global {
  interface Window {
    dataLayer: (
      | ITrackingVariable
      | ITrackingEvent
      | IEcommercePromoView
      | IEcommercePromoClick
      | ICartItems
      | IAddToCartEvent
      | IRemoveFromCartEvent
      | ICheckoutStepEvent
      | IAddShippingInfoEvent
      | IAddPaymentInfoEvent
      | IPurchaseEvent
      | IEmptyEcommerceObject
    )[];
    ga: ((send: "send", event: "event", content: "Content", description: string, title: string) => void) | undefined;
  }
}

// GTM standard for arbitrary events (to be used in GTM triggers of the type "custom event")
interface ITrackingEvent {
  event: string;
}

// Allow cleaning/emptying the ecommerce object in the data layer by pushing null into it
interface IEmptyEcommerceObject {
  ecommerce: null;
}

// GTM standard für arbitrary variables
interface ITrackingVariable {
  [name: string]: string;
}

// region Data layer Ecommerce interfaces

/* The following interfaces depict the data layer format used by GTM for Google Analytics Enhanced Ecommerce tracking
 * Event names are arbitrary, but they MUST match the custom event trigger in GTM
 * For more information on the data structures, see: https://developers.google.com/tag-manager/enhanced-ecommerce
 *
 * NOTE:
 * This is partially obsolete as of July 2023, because Universal Analytics (UA) is replaced by Google Analytics 4 (GA4).
 * GA4 works with the old data structures (for now), but this should be reworked in the future (see FE-100704). */

export interface ICustomerData {
  Salutation: string;
  Email: string;
  Birthday: string;
}

// Promotion impression / view
interface IEcommercePromoView {
  ecommerce: {
    promoView: {
      promotions: IPromotionTrackingData[];
    };
  };
  event: "trackPromoImpressions";
}

// Promotion interaction / click
interface IEcommercePromoClick {
  ecommerce: {
    promoClick: {
      promotions: IPromotionTrackingData[];
    };
  };
  event: "trackPromoClick";
}

interface ICartItems {
  CartItems: IProductTrackingData[];
}

// Internal promotion attributes / metadata
interface IPromotionTrackingData {
  id: string;
  name: string;
  position: string;
}

interface IAddToCartEvent {
  ecommerce: {
    currencyCode: string;
    add: {
      actionField?: {
        list: string;
      };
      products: IProductTrackingData[];
    };
  };
  event: "trackProductAddToCart";
}

interface IRemoveFromCartEvent {
  ecommerce: {
    remove: {
      products: IProductTrackingData[];
    };
  };
  event: "trackProductRemoveFromCart";
}

type IItemListItem = {item_id: number; quantity: number};
type IWishlistTracking = {
  items: IItemListItem[];
  item_id: number;
  item_price: string;
  item_sku: number;
  item_name: string;
  item_quantity: number;
  total_price: number;
  total_quantity: number;
};

interface IAddToWishlistEvent {
  ecommerce: {
    wishlistAdd: {
      products: IWishlistTracking;
    };
  };
  event: "trackProductAddToWishlist";
}

interface IRemoveFromWishlistEvent {
  ecommerce: {
    wishlistRemove: {
      products: IWishlistTracking;
    };
  };
  event: "trackProductRemoveFromWishlist";
}

interface ICheckoutStepEvent {
  ecommerce: {
    checkout: {
      actionField: {
        step: number;
      };
      products: IProductTrackingData[];
    };
  };
  event: "trackCheckoutStep";
}

interface IAddShippingInfoEvent {
  ecommerce: {
    shipping_tier: string;
    items: IProductTrackingData[];
  };
  event: "add_shipping_info";
}

interface IAddPaymentInfoEvent {
  ecommerce: {
    payment_type: string;
    items: IProductTrackingData[];
  };
  event: "add_payment_info";
}

interface IPurchaseEvent {
  ecommerce: {
    purchase: {
      actionField: IPurchaseSummaryInformation;
      products: IProductTrackingData[];
    };
  };
  event: "trackPurchase";
}

interface IEcommerceProductImpression {
  ecommerce: {
    currencyCode: string;
    impressions: IProductImpressionData[];
  };
  event: "trackProductImpressions";
}

interface IEcommerceImpressionClicked {
  ecommerce: {
    click: {
      actionField: {
        list: string;
      };
      products: IProductClickedData[];
    };
  };
  event: "trackProductClick";
}

// Product attributes / metadata
export interface IProductTrackingData {
  name: string;
  id: number;
  price: string;
  brand: string | null;
  quantity: number;
}

// Purchase information / metadata
export interface IPurchaseSummaryInformation {
  id: number;
  affiliation: number;
  revenue: string;
  tax: string;
  shipping: string;
  coupon: string;
}

interface IProductClickedData extends Omit<IProductTrackingData, "quantity"> {
  position: number;
}

interface IProductImpressionData extends IProductClickedData {
  list: string;
}

interface IIndexedItems {
  index: number;
  item: IItemContent;
}

// endregion Data layer Ecommerce interfaces

class Tracking {
  public constructor() {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
  }

  get data() {
    return window.dataLayer;
  }

  private cleanEcommerce() {
    this.data.push({ecommerce: null});
  }

  public setCartItems(cartItems: IProductTrackingData[]): void {
    const data: ICartItems = {
      CartItems: cartItems,
    };
    this.data.push(data);
  }

  public setVariable(name: string, value: string): void {
    const data: ITrackingVariable = {[name]: value};
    this.data.push(data);
  }

  public sendEvent(eventName: string): void {
    const data: ITrackingEvent = {event: eventName};
    this.data.push(data);
  }

  public trackProductImpressions(items: IIndexedItems[], headline = ""): void {
    const data: IEcommerceProductImpression = {
      ecommerce: {
        currencyCode: window.c_ActiveCurrency,
        impressions: items.map<IProductImpressionData>((itm) => ({
          id: itm.item.itemId,
          name: itm.item.title,
          brand: itm.item.manufacturer || "",
          price: itm.item.prices[0].price.formatted,
          list: headline,
          position: itm.index + 1,
        })),
      },
      event: "trackProductImpressions",
    };
    this.cleanEcommerce();
    this.data.push(data);
  }

  public trackImpressionClicked(item: IItemContent, index: number, headline = ""): void {
    const data: IEcommerceImpressionClicked = {
      ecommerce: {
        click: {
          actionField: {
            list: headline,
          },
          products: [
            {
              id: item.itemId,
              name: item.title,
              brand: item.manufacturer || "",
              price: item.prices[0].price.formatted,
              position: index + 1,
            },
          ],
        },
      },
      event: "trackProductClick",
    };
    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendPromoView(elem: HTMLElement): void {
    const data: IEcommercePromoView = {
      ecommerce: {
        promoView: {
          promotions: [Tracking.createPromotionData(elem)],
        },
      },
      event: "trackPromoImpressions",
    };
    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendPromoClick(elem: HTMLElement): void {
    const data: IEcommercePromoClick = {
      ecommerce: {
        promoClick: {
          promotions: [Tracking.createPromotionData(elem)],
        },
      },
      event: "trackPromoClick",
    };
    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendAddToCart(productData: IProductTrackingData[], currency: string, productListName?: string): void {
    const data: IAddToCartEvent = {
      ecommerce: {
        add: {
          products: productData,
        },
        currencyCode: currency,
      },
      event: "trackProductAddToCart",
    };
    if (productListName) {
      data.ecommerce.add.actionField = {list: productListName};
    }

    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendRemoveFromCart(productData: IProductTrackingData): void {
    const data: IRemoveFromCartEvent = {
      ecommerce: {
        remove: {
          products: [productData],
        },
      },
      event: "trackProductRemoveFromCart",
    };

    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendAddToWishlist(productList: IWishlistItem[], itemOfInterest: IWishlistItem): void {
    const data: IAddToWishlistEvent = {
      ecommerce: {
        wishlistAdd: {
          products: Tracking.buildWishlistObject(productList, itemOfInterest),
        },
      },
      event: "trackProductAddToWishlist",
    };

    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendRemoveFromWishlist(productList: IWishlistItem[], itemOfInterest: IWishlistItem): void {
    const data: IRemoveFromWishlistEvent = {
      ecommerce: {
        wishlistRemove: {
          products: Tracking.buildWishlistObject(productList, itemOfInterest),
        },
      },
      event: "trackProductRemoveFromWishlist",
    };

    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendCheckoutStepEvent(currentStep: string, cartProducts: IProductTrackingData[]): void {
    let stepNumber = 0;
    switch (currentStep) {
      case "login":
        stepNumber = 1;
        break;
      case "address":
        stepNumber = 2;
        break;
      case "shipping":
        stepNumber = 4;
        break;
      case "payment":
        stepNumber = 5;
        break;
      case "summary":
        stepNumber = 6;
        break;
    }
    const data: ICheckoutStepEvent = {
      ecommerce: {
        checkout: {
          actionField: {
            step: stepNumber,
          },
          products: cartProducts,
        },
      },
      event: "trackCheckoutStep",
    };
    this.cleanEcommerce();
    this.setCartItems(cartProducts);
    this.data.push(data);
  }

  public sendAddShippingInfoEvent(shippingTier: string, cartProducts: IProductTrackingData[]): void {
    const data: IAddShippingInfoEvent = {
      ecommerce: {
        shipping_tier: shippingTier,
        items: cartProducts,
      },
      event: "add_shipping_info",
    };

    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendAddPaymentInfoEvent(paymentType: string, cartProducts: IProductTrackingData[]): void {
    const data: IAddPaymentInfoEvent = {
      ecommerce: {
        payment_type: paymentType,
        items: cartProducts,
      },
      event: "add_payment_info",
    };

    this.cleanEcommerce();
    this.data.push(data);
  }

  public sendPurchaseEvent(
    summary: IPurchaseSummaryInformation,
    cartProducts: IProductTrackingData[],
    customerData: ICustomerData,
  ): void {
    const data: IPurchaseEvent = {
      ecommerce: {
        purchase: {
          actionField: summary,
          products: cartProducts,
        },
      },
      event: "trackPurchase",
    };

    this.setVariable("CustomerOrder.Salutation", customerData.Salutation);
    this.setVariable("CustomerOrder.Email", customerData.Email);
    this.setVariable("CustomerOrder.Birthday", customerData.Birthday);
    this.cleanEcommerce();
    this.data.push(data);
  }

  private static createPromotionData(elem: HTMLElement): IPromotionTrackingData {
    return {
      id: elem.dataset.gaPromoLinkid || `Media${elem.dataset.gaPromoMediaid || ""}`,
      name: elem.dataset.gaPromoName || elem.querySelector("img")?.alt || elem.innerText,
      position: elem.dataset.gaPromoPosition || "",
    };
  }

  /**
   * build default cart/wishlist action tracking object
   * @param listItems items AFTER action is done
   * @param itemOfInterest item thats added or deleted
   * @private
   */
  private static buildWishlistObject(listItems: IWishlistItem[], itemOfInterest: IWishlistItem): IWishlistTracking {
    const {itemId, title, prices} = itemOfInterest.item;

    return {
      items: listItems.map((wlItem) => ({item_id: wlItem.item.itemId, quantity: 1})),
      item_id: itemId,
      item_name: title,
      item_price: prices[0]?.price.amount,
      item_quantity: 1,
      item_sku: itemId,
      total_price: listItems.reduce((aggregate, current) => {
        return aggregate + Number(current.item.prices[0].price.amount);
      }, 0),
      total_quantity: listItems.length,
    };
  }
}

const tracking = new Tracking();
export {tracking as Tracking};
